"use client"

import { FormSection } from "@/(admin)/components/form"
import LiveNewsItem from "@/(admin)/modules/post/pages/LiveNewsItem"
import VideoModal from "@/(admin)/modules/ui-builder/config/components/VideoModal"
import { extractTwitterId } from "@/(admin)/modules/ui-builder/config/lib/twitter-helpers"
import { extractYouTubeId, getYouTubeThumbnail } from "@/(admin)/modules/ui-builder/config/lib/youtube-helpers"
import { useDebounce } from "@/hook/useDebounce"
import { t } from "@/languages/i18n"
import { FieldType, LiveNewsItemData, MediaData, PostFormProps } from "@/types/types"
import { buildMediaHTML } from "@/utils/guessMimeFromUrl"
import { generateSlug } from "@/utils/remove-accents"
import { EditOutlined, LockOutlined, PlusOutlined } from "@ant-design/icons"
import { App, Button, Form, Input, Modal, Switch } from "antd"
import dynamic from "next/dynamic"
import React, { useEffect, useRef, useState } from "react"
import ImageAlbumModal from "../../../../lib/config/lib/ImageAlbumModal"
import { registerAlbumBridge, setAlbumEditorRef } from "../../../../lib/config/lib/ImageAlbumPlugin"
import { slugPattern } from "../../../../utils/validationPatterns"
import Media from "../../media/pages/Media"
import { checkSlugAvailability, generateUniqueSlug } from "../services/post"
import PromoVideoInput from "./PromoVideoInput"

const CKEditorComponent = dynamic(() => import("../../../../lib/config/lib/Ckeditor"), { ssr: false })

const isValidJson = (str: string): boolean => {
  try {
    JSON.parse(str)
    return true
  } catch {
    return false
  }
}

const PostForm: React.FC<PostFormProps> = ({
  form,
  onFinishFailed,
  onValidationErrorsChange,
  onFinish,
  initialLiveNewsItems = [],
  initialIsLiveNews = false,
  onChangeContent,
  onValuesChange,
  onEditorReadyCb,
  initialSlug,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  // Slug mặc định mở (sửa được) và tự sinh theo tiêu đề. Bấm "Khoá" → cố định,
  // không đổi theo tiêu đề nữa. Bài đã có slug (edit / dịch) thì khởi tạo ở trạng thái khoá.
  const [isSlugLocked, setIsSlugLocked] = useState(!!initialSlug?.trim())
  // Kết quả kiểm tra trùng gần nhất (slug nào + có bị bài khác dùng không). Chỉ cho
  // khoá khi slug đang hiển thị ĐÃ được kiểm tra xong và không trùng — chặn cả khe
  // debounce/API đang chạy.
  const [slugCheck, setSlugCheck] = useState<{ value: string; taken: boolean } | null>(null)
  const [liveNewsItems, setLiveNewsItems] = useState<LiveNewsItemData[]>(initialLiveNewsItems)
  const [isLiveNews, setIsLiveNews] = useState(initialIsLiveNews)
  const editorRef = useRef<any>(null)
  const originalTitleRef = useRef<string>("")
  const originalSlugRef = useRef<string>(initialSlug?.trim() ?? "")
  // auto mode: generate slug from title. Disabled when slug comes from DB, user locks it, or user types it manually.
  const slugAutoModeRef = useRef(true)
  // Bản state của slugAutoModeRef — chỉ để render dòng gợi ý dưới ô slug cho đúng
  // (ref không gây re-render). Luôn đổi qua setSlugAutoMode để 2 nơi không lệch nhau.
  const [isSlugAutoMode, setIsSlugAutoMode] = useState(true)
  const setSlugAutoMode = (value: boolean) => {
    slugAutoModeRef.current = value
    setIsSlugAutoMode(value)
  }
  const slugManuallyEditedRef = useRef(false)

  const watchedSlug = Form.useWatch("slug", form)
  const debouncedSlug = useDebounce(watchedSlug, 500)
  const postId: number | undefined = form.getFieldValue("id") || undefined
  const [previewVideo, setPreviewVideo] = useState<{ url: string; open: boolean }>({ url: "", open: false });
  const { message } = App.useApp()

  useEffect(() => {
    registerAlbumBridge()
  }, [])

  useEffect(() => {
    setLiveNewsItems(initialLiveNewsItems)
    setIsLiveNews(initialIsLiveNews)
  }, [initialLiveNewsItems, initialIsLiveNews])

  // Keep originalSlugRef in sync when the parent provides the server slug (edit
  // mode) — hoặc slug đã tính sẵn đúng format cho bản dịch (xem TranslatePost:
  // slug gốc + hậu tố locale, vd "jar-vi"). Một khi có initialSlug thật (không
  // rỗng), tắt luôn slugAutoMode: đây là slug CÓ CHỦ ĐÍCH, không phải chỗ trống
  // chờ auto-generate — blur tiêu đề (kể cả không sửa gì) không được ghi đè nó.
  // Trước đây slugAutoModeRef chỉ tắt khi user tự bấm "Chỉnh sửa" rồi khoá lại,
  // nên slug ngắn tự đặt tay (vd "jar" cho bài khuyến mãi tamdafoods) bị regenerate
  // lại theo tiêu đề ngay lần đầu blur title, dù chưa sửa gì.
  useEffect(() => {
    if (initialSlug !== undefined) {
      originalSlugRef.current = initialSlug.trim()
      if (initialSlug.trim()) {
        setSlugAutoMode(false)
        setIsSlugLocked(true)
      }
    }
  }, [initialSlug])

  useEffect(() => {
    const currentTitle = form.getFieldValue("title") || ""
    originalTitleRef.current = currentTitle.trim()
    if (initialLiveNewsItems.length > 0) {
      const formIsLiveNews = form.getFieldValue("isLiveNews")
      if (formIsLiveNews !== undefined && formIsLiveNews !== isLiveNews) {
        setIsLiveNews(formIsLiveNews)
      }
    }
  }, [form, initialLiveNewsItems.length])

  useEffect(() => {
    const validateSlug = async () => {
      const trimmed = debouncedSlug?.trim() ?? ""

      // No input or slug unchanged from the server-saved value → always valid.
      if (!trimmed || trimmed === originalSlugRef.current) {
        onValidationErrorsChange?.([])
        form.setFields([{ name: "slug", errors: [] }])
        setSlugCheck({ value: trimmed, taken: false })
        return
      }

      if (!slugPattern.test(trimmed)) {
        return
      }

      try {
        // Pass postId so the backend can exclude the current record from its check.
        const response = await checkSlugAvailability(trimmed, postId)
        if (response && response.isAvailable === false) {
          setSlugCheck({ value: trimmed, taken: true })
          form.setFields([
            {
              name: "slug",
              errors: [t("Slug đã tồn tại. Vui lòng chọn slug khác.")],
            },
          ])
          onValidationErrorsChange?.([
            {
              fieldName: "slug",
              message: t("Slug đã tồn tại. Vui lòng chọn slug khác."),
            },
          ])
        } else {
          onValidationErrorsChange?.([])
          form.setFields([{ name: "slug", errors: [] }])
          setSlugCheck({ value: trimmed, taken: false })
        }
      } catch {
        onValidationErrorsChange?.([])
        form.setFields([{ name: "slug", errors: [] }])
        setSlugCheck({ value: trimmed, taken: false })
      }
    }

    validateSlug()
  }, [debouncedSlug, postId, form])

  const handleSubmit = () => {
    const formData = form.getFieldsValue()
    formData.liveNewsItem = liveNewsItems
    formData.isLiveNews = isLiveNews
    const rawSlug = formData.slug || ""
    formData.slug = generateSlug(rawSlug)
    onFinish(formData)
  }

  const handleAddLiveNewsItem = () => {
    const newItem: LiveNewsItemData = {
      id: Date.now(),
      title: "",
      date: "",
      detail: "",
    }
    const updatedItems = [...liveNewsItems, newItem]
    setLiveNewsItems(updatedItems)
    form.setFieldValue("liveNewsItem", updatedItems)
    if (onValuesChange) onValuesChange()
  }

  const handleUpdateLiveNewsItem = (index: number, data: LiveNewsItemData) => {
    const updatedItems = [...liveNewsItems]
    updatedItems[index] = data
    setLiveNewsItems(updatedItems)
    form.setFieldValue("liveNewsItem", updatedItems)
    if (onValuesChange) onValuesChange()
  }

  const handleDeleteLiveNewsItem = (index: number) => {
    const updatedItems = liveNewsItems.filter((_, i) => i !== index)
    setLiveNewsItems(updatedItems)
    form.setFieldValue("liveNewsItem", updatedItems)
    if (onValuesChange) onValuesChange()
  }

  const handleOpenModal = () => setIsModalOpen(true)
  const handleCloseModal = () => setIsModalOpen(false)

  const handleSelectMedia = (media: MediaData | MediaData[]) => {
    const items = Array.isArray(media) ? media : [media]
    const html = items.map(m => buildMediaHTML(m)).join("\n")

    if (editorRef.current) {
      try {
        const model = editorRef.current.model
        const selection = model.document.selection
        const viewFragment = editorRef.current.data.processor.toView(html)
        const modelFragment = editorRef.current.data.toModel(viewFragment)
        model.insertContent(modelFragment, selection)
      } catch {
        const current = form.getFieldValue("content") || ""
        form.setFieldsValue({ content: `${current}${html}` })
      }
    } else {
      const current = form.getFieldValue("content") || ""
      form.setFieldsValue({ content: `${current}${html}` })
    }

    setTimeout(() => form.validateFields(["content"]), 100)
    setIsModalOpen(false)
  }

  const handleTitleBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    const titleValue = e.target.value.trim()
    if (!titleValue) return

    const currentSlug = (form.getFieldValue("slug") || "").trim()
    // Auto-generate when: still in auto mode OR slug field is empty
    if (!slugAutoModeRef.current && currentSlug) return

    const uniqueSlug = await generateUniqueSlug(titleValue, postId)
    form.setFieldsValue({ slug: uniqueSlug })
    onValuesChange?.()
  }

  const handleSlugBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    const raw = e.target.value
    const normalized = generateSlug(raw)

    if (!normalized) {
      const titleValue = (form.getFieldValue("title") || "").trim()
      if (titleValue) {
        const uniqueSlug = await generateUniqueSlug(titleValue, postId)
        form.setFieldsValue({ slug: uniqueSlug })
        setSlugAutoMode(true)
        slugManuallyEditedRef.current = false
        form.validateFields(["slug"])
        onValuesChange?.()
      }
      return
    }

    if (normalized !== raw) {
      form.setFieldsValue({ slug: normalized })
      form.validateFields(["slug"])
      onValuesChange?.()
    }

    const trimmed = normalized.trim()
    if (!trimmed || trimmed === originalSlugRef.current) {
      return
    }
    if (!slugPattern.test(trimmed)) {
      return
    }

    try {
      const response = await checkSlugAvailability(trimmed, postId)
      if (response && response.isAvailable === false) {
        setSlugCheck({ value: trimmed, taken: true })
        form.setFields([
          {
            name: "slug",
            errors: [t("Slug đã tồn tại. Vui lòng chọn slug khác.")],
          },
        ])
        onValidationErrorsChange?.([
          {
            fieldName: "slug",
            message: t("Slug đã tồn tại. Vui lòng chọn slug khác."),
          },
        ])
      } else {
        onValidationErrorsChange?.([])
        form.setFields([{ name: "slug", errors: [] }])
        setSlugCheck({ value: trimmed, taken: false })
      }
    } catch {
      onValidationErrorsChange?.([])
      form.setFields([{ name: "slug", errors: [] }])
      setSlugCheck({ value: trimmed, taken: false })
    }
  }

  const trimmedWatchedSlug = watchedSlug?.trim() ?? ""
  const canLockSlug =
    !!trimmedWatchedSlug &&
    slugPattern.test(trimmedWatchedSlug) &&
    slugCheck?.value === trimmedWatchedSlug &&
    slugCheck?.taken === false

  const handleToggleSlugLock = () => {
    if (!isSlugLocked) {
      // Khoá → giữ cố định, tiêu đề đổi cũng không ảnh hưởng.
      setSlugAutoMode(false)
    } else {
      // Mở khoá → chỉ quay lại auto khi slug chưa từng được đặt có chủ đích
      // (không có slug từ DB/bản dịch và user chưa tự gõ).
      setSlugAutoMode(!originalSlugRef.current && !slugManuallyEditedRef.current)
    }
    setIsSlugLocked(!isSlugLocked)
  }

  const handleSlugChange = () => {
    // User tự gõ slug → không ghi đè bằng slug sinh từ tiêu đề nữa.
    slugManuallyEditedRef.current = true
    setSlugAutoMode(false)
  }

  return (
    <Form
      form={form}
      name="post"
      initialValues={{ remember: true }}
      onFinish={handleSubmit}
      onFinishFailed={onFinishFailed}
      autoComplete="off"
      layout="vertical"
      onValuesChange={onValuesChange}
      requiredMark="optional"
    >
      <FormSection title={t("Cơ bản")} collapsible={false}>
        <Form.Item<FieldType>
          name="title"
          label={t("Tiêu đề")}
          rules={[{ required: true, message: t("Vui lòng nhập tên bài viết!") }]}
        >
          <Input placeholder={t("VD: Sự kiện khai trương chi nhánh mới")} onBlur={handleTitleBlur} />
        </Form.Item>

        <Form.Item<FieldType>
          name="slug"
          label={t("Đường dẫn")}
          extra={
            isSlugLocked
              ? t("Đã khoá. Bấm Chỉnh sửa để đổi.")
              : isSlugAutoMode
                ? t("Tự động sinh từ tiêu đề. Bấm Khoá để giữ cố định.")
                : undefined
          }
          rules={[
            {
              pattern: slugPattern,
              message: "Chỉ dùng A-Z, a-z, 0-9, - , _ , . , ~",
            },
            { required: true, message: t("Vui lòng thêm đường dẫn!") },
          ]}
        >
          <Input
            disabled={isSlugLocked}
            placeholder={t("vd-duong-dan-bai-viet")}
            onBlur={handleSlugBlur}
            onChange={handleSlugChange}
            addonAfter={
              <Button
                type="text"
                size="small"
                icon={isSlugLocked ? <EditOutlined /> : <LockOutlined />}
                onClick={handleToggleSlugLock}
                // Chỉ cho khoá slug hợp lệ: không trống, đúng format, chưa bị bài khác dùng.
                disabled={!isSlugLocked && !canLockSlug}
              >
                {isSlugLocked ? t("Chỉnh sửa") : t("Khoá")}
              </Button>
            }
          />
        </Form.Item>

        <Form.Item<FieldType> name="description" label={t("Mô tả ngắn")}>
          <Input.TextArea
            rows={3}
            placeholder={t("Tóm tắt 1-2 câu hiển thị ở danh sách + SEO")}
            showCount
            maxLength={300}
          />
        </Form.Item>
      </FormSection>

      <FormSection title={t("Nội dung")}>
        {/* Không bắt buộc ở mức Form.Item nữa: lưu nháp chỉ cần title + slug.
            Bắt buộc nội dung khi XUẤT BẢN được kiểm tra thủ công ở onFinish của
            trang cha (UpsertPost/TranslatePost), giống cách website/ảnh đại diện
            đã làm — vì "required" tĩnh ở đây sẽ chặn cả lưu nháp. */}
        <Form.Item<FieldType> name="content" className="mb-0">
          <div>
            <CKEditorComponent
              value={form.getFieldValue("content")}
              onInsertMedia={handleOpenModal}
              onChange={(data: string) => {
                form.setFieldsValue({ content: data })
                onChangeContent?.()
              }}
              onEditorReady={(editor: any) => {
                editorRef.current = editor
                setAlbumEditorRef(editor)
                const cleanData = editor.getData()
                form.setFieldsValue({ content: cleanData })
                onEditorReadyCb?.()
              }}
            />
          </div>
        </Form.Item>
      </FormSection>

      <FormSection title={t("Video quảng bá")} defaultOpen={false}
        description={
          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.promo_video_urls !== curr.promo_video_urls}
          >
            {({ getFieldValue }) => {
              const urls: string[] = getFieldValue("promo_video_urls") || []
              const validCount = urls.filter((url: string) => !!extractYouTubeId(url) || !!extractTwitterId(url)).length
              return validCount > 0 ? `${t("Hiện đang có")} ${validCount} ${t("video")}` : t("Nếu có")
            }}
          </Form.Item>
        }
      >
        <Form.List name="promo_video_urls">
          {(fields, { add, remove }) => (
            <div className="flex flex-col gap-10">
              {fields.map(({ key, ...field }) => (
                <Form.Item
                  key={key}
                  {...field}

                  className="mb-2"

                  validateTrigger={['onChange', 'onBlur']}

                  rules={[
                    {
                      validator: async (_, value: string) => {

                        if (!value || value.trim() === "") {
                          return Promise.resolve();
                        }

                        const twId = extractTwitterId(value);
                        if (twId) return Promise.resolve();

                        const ytId = extractYouTubeId(value);
                        if (ytId) {

                          return new Promise((resolve, reject) => {
                            const img = new Image();
                            const timeout = setTimeout(() => {
                              reject(new Error(t("Không thể kết nối tới YouTube để kiểm tra")));
                            }, 3000);

                            img.onload = () => {
                              clearTimeout(timeout);
                              if (img.naturalWidth <= 120) {
                                reject(new Error(t("Video YouTube không tồn tại")));
                              } else {
                                resolve(null);
                              }
                            };
                            img.onerror = () => {
                              clearTimeout(timeout);
                              reject(new Error(t("Đường dẫn YouTube không đúng")));
                            };
                            img.src = getYouTubeThumbnail(ytId);
                          });
                        }

                        return Promise.reject(new Error(t("Đường dẫn không hợp lệ")));
                      }
                    }
                  ]}
                >
                  <PromoVideoInput onRemove={() => remove(field.name)} onPreview={(url) => setPreviewVideo({ url, open: true })} />
                </Form.Item>
              ))}
              <Button type="dashed" icon={<PlusOutlined />} onClick={() => add("")} className="w-fit">
                {t("Thêm đường dẫn video (YouTube, X/Twitter)")}
              </Button>
            </div>
          )}
        </Form.List>
      </FormSection>

      <FormSection
        title={t("Tin tường thuật trực tiếp")}
        defaultOpen={false}
        description={t("Chỉ bật khi bài viết tường thuật sự kiện đang diễn ra")}
      >
        <Form.Item<FieldType> name="isLiveNews" label={t("Bật chế độ tường thuật")} valuePropName="checked">
          <Switch
            onChange={checked => {
              setIsLiveNews(checked)
              if (!checked) {
                setLiveNewsItems([])
                form.setFieldValue("liveNewsItem", [])
              }
              onValuesChange?.()
            }}
          />
        </Form.Item>

        {isLiveNews && (
          <>
            <div className="flex items-center justify-between mb-3">
              <span style={{ fontSize: 13, fontWeight: 500, color: "var(--admin-foreground)" }}>
                {t("Danh sách cập nhật")}
              </span>
              <Button icon={<PlusOutlined />} onClick={handleAddLiveNewsItem}>
                {t("Thêm cập nhật")}
              </Button>
            </div>
            <div className="flex flex-col gap-3">
              {liveNewsItems.map((item, index) => (
                <LiveNewsItem
                  key={item.id || index}
                  index={index}
                  data={item}
                  onChange={(data: LiveNewsItemData) => handleUpdateLiveNewsItem(index, data)}
                  onDelete={() => handleDeleteLiveNewsItem(index)}
                />
              ))}
            </div>

            <Form.Item<FieldType>
              name="isLiveHappening"
              label={t("Đang diễn ra (live)")}
              valuePropName="checked"
              className="mt-4 mb-0"
            >
              <Switch />
            </Form.Item>
          </>
        )}
      </FormSection>

      <FormSection title="SEO" defaultOpen={false} description={t("Tuỳ biến khi cần")}>
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item<FieldType> name="metaTitle" label={t("Meta title")}>
            <Input placeholder={t("Mặc định lấy từ tiêu đề")} />
          </Form.Item>

          <Form.Item<FieldType> name="keywords" label={t("Từ khoá")}>
            <Input placeholder={t("Cách nhau bằng dấu phẩy")} />
          </Form.Item>

          <Form.Item<FieldType> name="metaDescription" label={t("Meta description")} className="col-span-2">
            <Input.TextArea rows={3} placeholder={t("Mặc định lấy từ mô tả ngắn")} showCount maxLength={160} />
          </Form.Item>

          <Form.Item<FieldType>
            name="structuredData"
            label={t("Structured data (JSON-LD)")}
            className="col-span-2"
            rules={[
              {
                validator: (_, value) => {
                  if (value && !isValidJson(value)) {
                    return Promise.reject(new Error(t("JSON không hợp lệ")))
                  }
                  return Promise.resolve()
                },
              },
            ]}
          >
            <Input.TextArea rows={4} placeholder='{"@context": "https://schema.org", ...}' />
          </Form.Item>

          <Form.Item<FieldType> name="metaRobots" label={t("Meta robots")}>
            <Input placeholder="index, follow" />
          </Form.Item>

          <Form.Item<FieldType> name="metaViewport" label={t("Meta viewport")}>
            <Input placeholder="width=device-width" />
          </Form.Item>

          <Form.Item<FieldType> name="canonicalURL" label={t("Canonical URL")} className="col-span-2">
            <Input placeholder="https://..." />
          </Form.Item>
        </div>
      </FormSection>

      <VideoModal
        isOpen={previewVideo.open}
        videoUrl={previewVideo.url}
        onClose={() => setPreviewVideo({ ...previewVideo, open: false })}
      />

      <Modal
        open={isModalOpen}
        title={t("Chọn ảnh")}
        onCancel={handleCloseModal}
        style={{ top: 5 }}
        width="95%"
        footer={null}
      >
        <Media
          isOpenModal={true}
          selectMode="multi"
          onSelectMedia={media => handleSelectMedia(media)}
        />
      </Modal>

      <ImageAlbumModal />
    </Form>
  )
}

export default PostForm
